﻿import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Modal, Button } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import Select from "react-select";
import { map } from "lodash";
import CustomInputSwitch from "@/components/desktop/form/input/input-switch/CustomInputSwitch";
import { toast, validRoles } from "@/services/Utils";

import classes from "../../maintainers.module.scss";
import Fetching from "@/components/Fetching/Fetching";

import { customStyles, base_element } from "../customers.constants";
import { useCustomer, useMutateSyncCustomerOrders } from "@/hooks/maintainers/customers";

import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";

const CustomersForm = ({ confirmSave, isLoadingSave, tools, countries, categories }) => {
	const { addToast } = useToasts();
	const location = useLocation();
	const { option, customer_id } = useParams();
	const navigate = useNavigate();

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isAdmin = validRoles(userRoles, ["administrador"]);

	const [data, setData] = useState({ ...base_element });

	const { data: response, isLoading } = useCustomer(customer_id);

	const { mutate: mutateSync, isLoading: isLoadingSync } = useMutateSyncCustomerOrders({
		customer_id,
	});

	useEffect(() => {
		if (response) {
			setData({ ...response.detail, use_estates_parents: true });
		}
	}, [response]);

	const handleClose = () => {
		if (location.state) {
			navigate(location.state);
			return;
		}

		navigate("/mantenedores/clientes");
	};

	const validSave = () => {
		if (!data.name) {
			toast(addToast, "Debe ingresar un nombre para guardar.", "error");
			return false;
		}
		if (!data.country_id) {
			toast(addToast, "Debe seleccionar el país del cliente para guardar.", "error");
			return false;
		}
		if (!data.category_id) {
			toast(addToast, "Debe seleccionar una categoría para guardar.", "error");
			return false;
		}

		return true;
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSave(data);
		}
	};

	const handleSyncOrders = () => {
		mutateSync(
			{ customer_id },
			{
				onSuccess: (sync_response) => {
					if (sync_response?.data?.delivery?.length === 0 && sync_response?.data?.sale?.length === 0) {
						addToast("No hay facturas/guías de despacho pendientes para sincronizar.", {
							appearance: "info",
						});
					} else {
						addToast("Facturas y Guías de despacho sincronizadas correctamente.", {
							appearance: "success",
						});
					}
				},
			},
		);
	};

	return (
		<Modal show={option ? true : false} onHide={handleClose} backdrop="static" keyboard={false} className={classes.main_form}>
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Cliente</h4>
			</Modal.Header>
			<Modal.Body>
				{isLoading || isLoadingSave || isLoadingSync ? (
					<Fetching topMargin={"1em"} />
				) : (
					<Row className={classes.row_form}>
						<Col md={12} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Nombre</Form.Label>
								<Form.Control
									value={data ? data.name : ""}
									onChange={(e) =>
										setData({
											...data,
											name: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>

							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>País</Form.Label>
								<Select
									isClearable={true}
									value={data && countries && countries.filter((b) => Number(b.value) === Number(data.country_id))}
									placeholder={<div>Seleccione</div>}
									options={data && countries ? countries : []}
									onChange={(value) => setData({ ...data, country_id: value ? value.value : null })}
									menuPlacement="auto"
								/>
							</Form.Group>

							<Row>
								<Col md={4} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>¿Usa Impresora ZPL?</Form.Label>
										<CustomInputSwitch
											value={Boolean(data.zpl_printer_configured)}
											optionLeft="NO"
											optionRight="SI"
											onChange={(checked) => {
												setData({ ...data, zpl_printer_configured: checked });
											}}
										/>
									</Form.Group>
								</Col>
								<Col md={4} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>¿Usa Sectores?</Form.Label>
										<CustomInputSwitch
											value={Boolean(data.use_sectors)}
											optionLeft="NO"
											optionRight="SI"
											onChange={(checked) => setData({ ...data, use_sectors: checked })}
										/>
									</Form.Group>
								</Col>
								<Col md={4} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Código OA</Form.Label>
										<CustomInputSwitch
											value={Boolean(data.use_code_oa)}
											optionLeft="NO"
											optionRight="SI"
											onChange={(checked) => setData({ ...data, use_code_oa: checked })}
										/>
									</Form.Group>
								</Col>
							</Row>

							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Categoría</Form.Label>
								<Select
									value={
										categories &&
										categories.filter(
											(a) =>
												data.category_id &&
												data.category_id.length > 0 &&
												map(data.category_id.split(","), String).includes(String(a.value)),
										)
									}
									styles={customStyles}
									isMulti
									placeholder={<div>Seleccione</div>}
									options={categories ? categories : []}
									onChange={(opt) => {
										let baseValue = [];
										opt.forEach((element) => {
											baseValue.push(element.value);
										});

										setData({
											...data,
											category_id: baseValue.length !== 0 ? baseValue.toString() : "",
										});
									}}
									components={{ NoOptionsMessage }}
								/>
							</Form.Group>

							<div className={classes.group_tools}>
								{tools &&
									tools.map((tool, i) => {
										return (
											<div
												key={i}
												className={classes.tool}
												onClick={() => {
													let newTools = [...data.tools];

													if (!newTools.includes(tool.value)) {
														newTools.push(tool.value);

														if ([2, 3].includes(tool.value) && data.id !== "") {
															handleSyncOrders();
														}
													} else {
														const index = newTools.indexOf(tool.value);
														if (index > -1) {
															newTools.splice(index, 1);
														}
													}

													setData({ ...data, tools: newTools });
												}}
											>
												<div className={`${classes.item} ${data.tools && data.tools.includes(tool.value) && classes.active}`}>
													{tool.label}
												</div>
											</div>
										);
									})}
							</div>
						</Col>
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer>
				{isAdmin && !isLoading && (
					<Button variant="success" onClick={handleSave} disabled={isLoading || (isLoadingSave && true)}>
						GUARDAR
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default CustomersForm;
